import axios, { AxiosInstance, AxiosError } from "axios";
import { toast } from "sonner";
import { authService } from "@/services/auth";
import {
  DatabaseConnection,
  TableDescription,
  Prompt,
  PromptRequest,
  SQLGeneration,
  SQLGenerationRequest,
  NLGeneration,
  NLGenerationRequest,
  GoldenSQL,
  Instruction,
  QueryResult,
  PaginationParams,
  AppConfig,
  Conversation,
  ConversationMessage,
  ConversationCreateRequest,
  ConversationUpdateRequest,
  MessageCreateRequest,
  ConversationSearchRequest,
  ConversationStatus,
  SemanticLayerDefinition,
} from "../types";

// Deprecated: Use apiService instance configuration instead
// export const API_URL = import.meta.env.VITE_API_URL || "http://localhost:8080";

class APIService {
  private client: AxiosInstance;
  private config: AppConfig;

  constructor() {
    this.config = this.loadConfig();

    console.log("this.config >>", this.config);
    this.client = axios.create({
      baseURL: `${this.config.engineUrl}/api/v1`,
      timeout: 150000, // 2.5 minutes
      headers: {
        "Content-Type": "application/json",
      },
    });

    // Request interceptor
    this.client.interceptors.request.use(
      (config) => {
        // Add auth headers if user is authenticated
        const token = authService.getAccessToken();
        if (token) {
          config.headers.Authorization = `Bearer ${token}`;
        }
        return config;
      },
      (error) => {
        return Promise.reject(error);
      },
    );

    // Response interceptor
    this.client.interceptors.response.use(
      (response) => response,
      (error: AxiosError) => {
        // Handle 401 Unauthorized - token expired or invalid
        if (error.response?.status === 401) {
          // Clear auth tokens silently (don't call backend API)
          authService.logoutSilent();

          // Show specific message for token expiration
          const errorData = error.response?.data as any;
          const isTokenExpired = errorData?.detail?.toLowerCase().includes('expired') ||
            errorData?.detail?.toLowerCase().includes('invalid token');

          if (isTokenExpired) {
            toast.error('Your session has expired. Please login again.');
          } else {
            toast.error('Authentication failed. Please login again.');
          }

          // Redirect to login page
          // Use setTimeout to avoid navigation during component render
          setTimeout(() => {
            window.location.href = '/login';
          }, 1000);

          return Promise.reject(error);
        }

        // Handle 429 Too Many Requests - token/rate limit exceeded
        if (error.response?.status === 429) {
          const errorData = error.response?.data as any;
          const detail = errorData?.detail || 'Rate limit exceeded';

          // Show user-friendly error message
          if (detail.toLowerCase().includes('token')) {
            toast.error('Token limit exceeded for your current plan. Please upgrade to continue.', {
              duration: 5000,
            });

            // Optionally navigate to pricing page after a delay
            setTimeout(() => {
              // Check if user wants to upgrade
              const shouldNavigate = window.confirm('Would you like to view available plans?');
              if (shouldNavigate) {
                window.location.href = '/pricing';
              }
            }, 2000);
          } else {
            toast.error(`Rate limit exceeded: ${detail}`, {
              duration: 5000,
            });
          }

          return Promise.reject(error);
        }

        // Handle other errors
        const message = this.getErrorMessage(error);
        toast.error(message);
        return Promise.reject(error);
      },
    );
  }

  private loadConfig(): AppConfig {
    // Try runtime environment variable from window, then build-time env var, then fallback
    const runtimeEngineUrl = (window as any).__ENV?.VITE_ENGINE_URL;
    const buildTimeEngineUrl = import.meta.env.VITE_ENGINE_URL;
    const engineUrl = runtimeEngineUrl || buildTimeEngineUrl || "http://localhost:8080";

    // Enhanced debugging
    console.log("Environment variables:", {
      RUNTIME_VITE_ENGINE_URL: runtimeEngineUrl,
      BUILD_VITE_ENGINE_URL: buildTimeEngineUrl,
      MODE: import.meta.env.MODE,
      PROD: import.meta.env.PROD,
      DEV: import.meta.env.DEV
    });
    console.log("Resolved engineUrl:", engineUrl);

    return {
      engineUrl,
      openaiApiKey: "",
      mongoDbUri: "",
      encryptKey: "",
    };
  }

  private getErrorMessage(error: AxiosError): string {
    if (error.response?.data && typeof error.response.data === "object") {
      const data = error.response.data as any;
      // If detail is an object, use message or description instead
      if (data.detail && typeof data.detail === "object") {
        return data.message || data.description || "An error occurred";
      }
      return data.detail || data.message || "An error occurred";
    }
    if (error.message) {
      return error.message;
    }
    return "An unexpected error occurred";
  }

  // Health Check
  async checkHealth(): Promise<boolean> {
    try {
      await this.client.get("/heartbeat");
      return true;
    } catch {
      return false;
    }
  }

  // Database Connections
  async createDatabaseConnection(
    connection: DatabaseConnection,
  ): Promise<DatabaseConnection> {
    const { data } = await this.client.post(
      "/database-connections",
      connection,
    );
    return data;
  }

  async getDatabaseConnections(): Promise<DatabaseConnection[]> {
    const { data } = await this.client.get("/database-connections");
    return data;
  }

  async updateDatabaseConnection(
    id: string,
    connection: Partial<DatabaseConnection>,
  ): Promise<DatabaseConnection> {
    const { data } = await this.client.put(
      `/database-connections/${id}`,
      connection,
    );
    return data;
  }

  async deleteDatabaseConnection(id: string): Promise<void> {
    await this.client.delete(`/database-connections/${id}`);
  }

  // ERD (Entity Relationship Diagram)
  async getERDGraph(
    dbConnectionId: string,
    schema?: string
  ): Promise<import('../types').ERDGraph> {
    const params = schema ? { schema } : {};
    const { data } = await this.client.get(
      `/database-connections/${dbConnectionId}/erd`,
      { params }
    );
    return data;
  }

  // Table Descriptions
  async syncSchemas(
    dbConnectionId: string,
    tableIds?: string[],
  ): Promise<TableDescription[]> {
    const { data } = await this.client.post(
      "/table-descriptions/sync-schemas",
      {
        db_connection_id: dbConnectionId,
        ids: tableIds || [],
      },
    );
    return data;
  }

  async refreshTableDescriptions(
    dbConnectionId: string,
    schemas?: string[],
  ): Promise<void> {
    await this.client.post("/table-descriptions/refresh", {
      db_connection_id: dbConnectionId,
      schemas,
    });
  }

  async getTableDescriptions(
    dbConnectionId: string,
  ): Promise<TableDescription[]> {
    const { data } = await this.client.get("/table-descriptions", {
      params: { db_connection_id: dbConnectionId },
    });
    return data;
  }

  async getTableDescription(id: string): Promise<TableDescription> {
    const { data } = await this.client.get(`/table-descriptions/${id}`);
    return data;
  }

  async updateTableDescription(
    id: string,
    description: Partial<TableDescription>,
  ): Promise<TableDescription> {
    const { data } = await this.client.put(
      `/table-descriptions/${id}`,
      description,
    );
    return data;
  }

  // Prompts
  async createPrompt(prompt: PromptRequest): Promise<Prompt> {
    const { data } = await this.client.post("/prompts", prompt);
    return data;
  }

  async getPrompts(dbConnectionId?: string): Promise<Prompt[]> {
    const { data } = await this.client.get("/prompts", {
      params: dbConnectionId ? { db_connection_id: dbConnectionId } : undefined,
    });
    return data;
  }

  async getPrompt(id: string): Promise<Prompt> {
    const { data } = await this.client.get(`/prompts/${id}`);
    return data;
  }

  // SQL Generation
  async generateSQL(
    promptId: string,
    request: SQLGenerationRequest = {},
  ): Promise<SQLGeneration> {
    const { data } = await this.client.post(
      `/prompts/${promptId}/sql-generations`,
      request,
    );
    return data;
  }

  async createPromptAndGenerateSQL(
    prompt: PromptRequest,
    sqlRequest: SQLGenerationRequest = {},
  ): Promise<SQLGeneration> {
    const { data } = await this.client.post("/prompts/sql-generations", {
      ...prompt,
      ...sqlRequest,
    });
    return data;
  }

  async getSQLGenerations(promptId?: string): Promise<SQLGeneration[]> {
    const { data } = await this.client.get("/sql-generations", {
      params: promptId ? { prompt_id: promptId } : undefined,
    });
    return data;
  }

  async getSQLGeneration(id: string): Promise<SQLGeneration> {
    const { data } = await this.client.get(`/sql-generations/${id}`);
    return data;
  }

  async executeSQL(
    sqlGenerationId: string,
    maxRows: number = 100,
  ): Promise<QueryResult> {
    const { data } = await this.client.get(
      `/sql-generations/${sqlGenerationId}/execute`,
      {
        params: { max_rows: maxRows },
      },
    );

    // Transform the response if it's an array of objects
    if (Array.isArray(data)) {
      // Get column names from the first object
      const columns = data.length > 0 ? Object.keys(data[0]) : [];

      // Transform rows from array of objects to array of arrays
      const rows = data.map((obj) => columns.map((col) => obj[col]));

      return {
        columns,
        rows,
        row_count: data.length,
      };
    }

    // Return as-is if it's already in the expected format
    return data;
  }

  async executeMessageSQL(
    messageId: string,
    maxRows: number = 100,
  ): Promise<ConversationMessage> {
    const { data } = await this.client.post(
      `/conversations/messages/${messageId}/execute`,
      null,
      {
        params: { max_rows: maxRows },
      },
    );
    return data;
  }

  async searchMessages(params: {
    query?: string;
    conversation_id?: string;
    message_type?: string;
    limit?: number;
    offset?: number;
  }): Promise<{
    messages: ConversationMessage[];
    total: number;
    limit: number;
    offset: number;
    has_more: boolean;
  }> {
    const { data } = await this.client.get("/messages/search", { params });
    return data;
  }

  async exportCSV(sqlGenerationId: string): Promise<Blob> {
    const { data } = await this.client.get(
      `/sql-generations/${sqlGenerationId}/csv-file`,
      {
        responseType: "blob",
      },
    );
    return data;
  }

  // NL Generation
  async generateNL(
    sqlGenerationId: string,
    request: NLGenerationRequest = {},
  ): Promise<NLGeneration> {
    const { data } = await this.client.post(
      `/sql-generations/${sqlGenerationId}/nl-generations`,
      request,
    );
    return data;
  }

  async createFullPipeline(
    prompt: PromptRequest,
    sqlRequest: SQLGenerationRequest = {},
    nlRequest: NLGenerationRequest = {},
  ): Promise<NLGeneration> {
    const { data } = await this.client.post(
      "/prompts/sql-generations/nl-generations",
      {
        ...prompt,
        ...sqlRequest,
        ...nlRequest,
      },
    );
    return data;
  }

  async getNLGenerations(sqlGenerationId?: string): Promise<NLGeneration[]> {
    const { data } = await this.client.get("/nl-generations", {
      params: sqlGenerationId
        ? { sql_generation_id: sqlGenerationId }
        : undefined,
    });
    return data;
  }

  // Golden SQLs
  async createGoldenSQL(
    goldenSQL: Omit<GoldenSQL, "id" | "created_at">,
  ): Promise<GoldenSQL> {
    const { data } = await this.client.post("/golden-sqls", goldenSQL);
    return data;
  }

  async getGoldenSQLs(
    params: PaginationParams & { db_connection_id?: string },
  ): Promise<GoldenSQL[]> {
    const { data } = await this.client.get("/golden-sqls", { params });
    return data;
  }

  async getGoldenSQL(id: string): Promise<GoldenSQL> {
    const { data } = await this.client.get(`/golden-sqls/${id}`);
    return data;
  }

  async updateGoldenSQL(
    id: string,
    update: Partial<GoldenSQL>,
  ): Promise<GoldenSQL> {
    const { data } = await this.client.put(`/golden-sqls/${id}`, update);
    return data;
  }

  async deleteGoldenSQL(id: string): Promise<void> {
    await this.client.delete(`/golden-sqls/${id}`);
  }

  // Instructions
  async createInstruction(
    instruction: Omit<Instruction, "id" | "created_at">,
  ): Promise<Instruction> {
    const { data } = await this.client.post("/instructions", instruction);
    return data;
  }

  async getInstructions(
    params: PaginationParams & { db_connection_id?: string },
  ): Promise<Instruction[]> {
    const { data } = await this.client.get("/instructions", { params });
    return data;
  }

  async updateInstruction(
    id: string,
    update: Partial<Instruction>,
  ): Promise<Instruction> {
    const { data } = await this.client.put(`/instructions/${id}`, update);
    return data;
  }

  async deleteInstruction(id: string): Promise<void> {
    await this.client.delete(`/instructions/${id}`);
  }

  // Streaming SQL Generation
  // Conversation Methods
  async createConversation(request: ConversationCreateRequest): Promise<Conversation> {
    const { data } = await this.client.post("/conversations", request);
    return data;
  }

  async listConversations(
    status?: ConversationStatus,
    limit: number = 50,
    offset: number = 0
  ): Promise<Conversation[]> {
    const { data } = await this.client.get("/conversations", {
      params: { status, limit, offset }
    });
    return data;
  }

  async searchConversations(request: ConversationSearchRequest): Promise<Conversation[]> {
    const { data } = await this.client.post("/conversations/search", request);
    return data;
  }

  async getConversation(conversationId: string): Promise<Conversation> {
    const { data } = await this.client.get(`/conversations/${conversationId}`);
    return data;
  }

  async updateConversation(
    conversationId: string,
    request: ConversationUpdateRequest
  ): Promise<Conversation> {
    const { data } = await this.client.put(`/conversations/${conversationId}`, request);
    return data;
  }

  async deleteConversation(conversationId: string): Promise<{ success: boolean; message: string }> {
    const { data } = await this.client.delete(`/conversations/${conversationId}`);
    return data;
  }

  async getConversationMessages(
    conversationId: string,
    limit?: number,
    offset: number = 0
  ): Promise<{
    messages: ConversationMessage[];
    total: number;
    limit: number;
    offset: number;
    has_more: boolean;
  }> {
    const { data } = await this.client.get(`/conversations/${conversationId}/messages`, {
      params: { limit: limit || 50, offset: offset || 0 }
    });
    return data;
  }

  async addConversationMessage(
    conversationId: string,
    request: MessageCreateRequest
  ): Promise<ConversationMessage> {
    const { data } = await this.client.post(`/conversations/${conversationId}/messages`, request);
    return data;
  }

  async processConversationQuery(
    conversationId: string,
    query: string
  ): Promise<{
    user_message: ConversationMessage;
    sql_message: ConversationMessage;
    prompt_id: string;
    sql_generation_id: string;
  }> {
    const { data } = await this.client.post(`/conversations/${conversationId}/query`, {
      query_text: query
    });
    return data;
  }

  async streamSQLGeneration(
    prompt: PromptRequest,
    onMessage: (data: any) => void,
    onError?: (error: any) => void,
    onComplete?: () => void,
  ): Promise<EventSource> {
    const eventSource = new EventSource(
      `${this.config.engineUrl}/api/v1/stream-sql-generation?${new URLSearchParams(
        {
          text: prompt.text,
          db_connection_id: prompt.db_connection_id,
          ...(prompt.schemas ? { schemas: prompt.schemas.join(",") } : {}),
        },
      )}`,
    );

    eventSource.onmessage = (event) => {
      try {
        const data = JSON.parse(event.data);
        onMessage(data);
      } catch (error) {
        console.error("Error parsing SSE data:", error);
      }
    };

    eventSource.onerror = (error) => {
      console.error("SSE Error:", error);
      if (onError) onError(error);
      eventSource.close();
      if (onComplete) onComplete();
    };

    return eventSource;
  }

  // Analytics
  async getDashboards(): Promise<import('../types').Dashboard[]> {
    const { data } = await this.client.get("/analytics/dashboards");
    return data;
  }

  async createDashboard(request: import('../types').CreateDashboardRequest): Promise<import('../types').Dashboard> {
    const { data } = await this.client.post("/analytics/dashboards", request);
    return data;
  }

  async createWidget(dashboardId: string, request: import('../types').CreateWidgetRequest): Promise<import('../types').Widget> {
    const { data } = await this.client.post(`/analytics/dashboards/${dashboardId}/widgets`, request);
    return data;
  }

  async getDashboard(dashboardId: string): Promise<import('../types').Dashboard> {
    const { data } = await this.client.get(`/analytics/dashboards/${dashboardId}`);
    return data;
  }

  async getWidgets(dashboardId: string): Promise<import('../types').Widget[]> {
    const { data } = await this.client.get(`/analytics/dashboards/${dashboardId}/widgets`);
    return data;
  }

  async getWidgetData(widgetId: string, refresh: boolean = false): Promise<import('../types').QueryResult> {
    const { data } = await this.client.get(`/analytics/widgets/${widgetId}/data`, { params: { refresh } });
    return data;
  }

  async deleteWidget(widgetId: string): Promise<void> {
    await this.client.delete(`/analytics/widgets/${widgetId}`);
  }

  async updateWidget(widgetId: string, request: Partial<import('../types').CreateWidgetRequest>): Promise<import('../types').Widget> {
    const { data } = await this.client.patch(`/analytics/widgets/${widgetId}`, request);
    return data;
  }

  // Usage and subscription methods
  async getCurrentUsage(): Promise<{
    requests_used: number;
    requests_limit: number | null;
    tokens_used: number;
    tokens_limit: number | null;
    connections_used: number;
    connections_limit: number | null;
    period_start: string;
    period_end: string;
  }> {
    const { data } = await this.client.get("/usage/current");
    return data;
  }

  async getCurrentSubscription(): Promise<{
    id: string;
    user_id: string;
    plan_id: string;
    status: string;
    current_period_start: string;
    current_period_end: string;
  }> {
    try {
      console.log('[API] Requesting GET /subscriptions/current');
      const { data } = await this.client.get("/subscriptions/current");
      console.log('[API] GET /subscriptions/current responded with:', data);
      return data;
    } catch (error) {
      console.error('[API] GET /subscriptions/current failed:', {
        error,
        errorMessage: error instanceof Error ? error.message : 'Unknown error',
        status: (error as any)?.response?.status,
        statusText: (error as any)?.response?.statusText,
        responseData: (error as any)?.response?.data,
      });
      throw error;
    }
  }

  // Payment and subscription checkout
  async createCheckoutSession(request: {
    plan_id: string;
    success_url: string;
    cancel_url: string;
  }): Promise<{
    subscription_id: string;
    plan_id: string;
    status: string;
    short_url?: string;
    key_id: string;
  }> {
    const { data } = await this.client.post("/subscriptions/checkout", request);
    return data;
  }

  // Verify payment and activate subscription
  async verifyPayment(request: {
    razorpay_subscription_id: string;
    razorpay_payment_id: string;
    razorpay_signature: string;
    plan_id: string;
  }): Promise<{
    success: boolean;
    message: string;
    subscription: any;
  }> {
    const { data } = await this.client.post("/subscriptions/verify-payment", request);
    return data;
  }

  // Semantic Layer
  async getSemanticDefinition(dbConnectionId: string): Promise<SemanticLayerDefinition> {
    const { data } = await this.client.get(`/semantic/definitions/${dbConnectionId}`);
    return data;
  }

  async addMetric(dbConnectionId: string, metric: any): Promise<SemanticLayerDefinition> {
    const { data } = await this.client.post("/semantic/metrics", { db_connection_id: dbConnectionId, metric });
    return data;
  }

  async addDimension(dbConnectionId: string, dimension: any): Promise<SemanticLayerDefinition> {
    const { data } = await this.client.post("/semantic/dimensions", { db_connection_id: dbConnectionId, dimension });
    return data;
  }

  async autoDiscoverSemantic(dbConnectionId: string): Promise<SemanticLayerDefinition> {
    const { data } = await this.client.post(`/semantic/auto-discover/${dbConnectionId}`);
    return data;
  }

  async executeSemanticQuery(dbConnectionId: string, metrics: string[], dimensions: string[], filters: any[] = []): Promise<import('../types').QueryResult> {
    const { data } = await this.client.post("/semantic/query", {
      db_connection_id: dbConnectionId,
      metrics,
      dimensions,
      filters
    });
    return data;
  }
}

export default new APIService();
